import { INotificationComponent } from '../../../../../../types/componentInterfaces/inotificationcomponent';
import { Component, inject, OnInit, output, viewChild } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { CommunityService, ModalService, NotificationService, OrgSelectionService, SubGroupsService } from '../../../../../../services';
import { ButtonText, Claims, Community, Market, Notification, SubGroup } from '../../../../../../types';
import { NotificationTypeEnum } from '../../../../../../types/enums/notificationtypeenum';
import { NotificationViewModel } from '../notificationviewmodel';
import { concat, concatMap, EMPTY, forkJoin, of, takeUntil, tap } from 'rxjs';
import { HasValuePipe } from '../../../../../../pipes/hasvalue.pipe';
import { CommandButtonComponent } from '../../../../../pcpcommandbar/commandbutton/commandbutton.component';
import { PcpCommandBarComponent } from '../../../../../pcpcommandbar/pcp-commandbar.component';
import { CheckAccessDirective } from '../../../../../../directives/access.directive';
import { CheckboxModule } from 'primeng/checkbox';
import { SharedModule } from 'primeng/api';
import { ListboxModule } from 'primeng/listbox';
import { SelectModule } from 'primeng/select';

import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { UnsubscribeOnDestroy } from '../../../../../../classes';

@Component({
    selector: 'changeorder-notifications',
    templateUrl: './changeorder.notifications.component.html',
    styleUrls: ['./changeorder.notifications.component.scss'],
    standalone: true,
    imports: [FormsModule, ToggleSwitchModule, SelectModule, ListboxModule, SharedModule, CheckboxModule, CheckAccessDirective, PcpCommandBarComponent, CommandButtonComponent, HasValuePipe]
})
export class ChangeOrderNotificationsComponent extends UnsubscribeOnDestroy implements OnInit, INotificationComponent {
    communityOptions: Community[];
    subGroupOptions: SubGroup[];
    subGroupCommunityOptions: any;
    market: Market;
    allCommChbx: boolean = false;
    isSwitchOn: boolean = false;
    ButtonText = ButtonText;
    notificationViewModel: NotificationViewModel = new NotificationViewModel();
    notification: Notification = new Notification();
    errorMessage = '';
    Claims = Claims;

    public get NotificationTypeEnum() {
        return NotificationTypeEnum;
    }

    readonly changeOrderNotificationForm = viewChild<NgForm>('changeOrderNotificationForm');

    readonly complete = output<boolean>();
    
    private communityService = inject(CommunityService);
    private modalService = inject(ModalService);
    private notificationService = inject(NotificationService);
    private orgSelectionService = inject(OrgSelectionService);
    private subGroupsService = inject(SubGroupsService);

    constructor() { super(); }

    ngOnInit(): void {
    }

    load(id: number) {
        this.errorMessage = "";
        this.getSelectedMarket(id);
    }

    getNotification$(notificationId: number) {
        if (notificationId > 0) { // edit
            return this.notificationService.getNotifications(this.market.orgId)
                .pipe(
                    concatMap((notifications) => {
                        this.notificationViewModel = new NotificationViewModel();
                        let notification = notifications.find((n) => n.notificationTypeId === 2);
                        this.notificationViewModel.setProperties(notification, this.communityOptions, this.subGroupOptions);
                        this.notification = notification;
                        this.notificationViewModel.selectedSubGroup ? this.isSwitchOn = true : this.isSwitchOn = false;
                        this.setCheckboxes();
                        return this.onHandleSubGroupChange$();
                    }));
        } else {
            this.changeOrderNotificationForm().form.reset();
            this.notificationViewModel = new NotificationViewModel();
            this.notification = new Notification();
            return EMPTY;
        }
    }

    getSelectedMarket(id) {
        this.communityOptions = [];
        this.subGroupOptions = [];
        this.orgSelectionService.selectedMarket
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                concatMap((market) => {
                    this.market = market;
                    return forkJoin({
                        communityOptions: this.communityService.getConstructionActiveCommunities(this.market.orgId),
                        subGroupOptions: this.subGroupsService.getSubGroups([this.market.orgId])
                    })
                }),
                concatMap(response => {
                    this.communityOptions = response.communityOptions;
                    this.subGroupOptions = response.subGroupOptions;
                    return this.getNotification$(id);
                })
            )
            .subscribe();
    }

    onHandleSubGroupChange$() {
        if (this.notificationViewModel.selectedSubGroup) {
            return this.subGroupsService.getSubGroup(this.notificationViewModel.selectedSubGroup.subGroupID)
                .pipe(
                    tap((subGroup) => {
                        this.subGroupCommunityOptions = subGroup.communities;
                    })
                );
        }
        else
            return EMPTY;
    }

    onHandleSubGroupChange() {
        this.onHandleSubGroupChange$()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe();
    }

    onHandleCommChange(e) {
        this.allCommChbx = false;
    }

    onHandleCommChbxChange(e) {
        if (e.checked) {
            this.notificationViewModel.selectedCommunities = [];
        }
    }

    setCheckboxes() {
        this.notificationViewModel?.selectedCommunities.length === 0 && !this.notificationViewModel.selectedSubGroup ? this.allCommChbx = true : this.allCommChbx = false;
    }

    onClose() {
        this.complete.emit(false);
        this.changeOrderNotificationForm().form.reset();
    }

    onCancel() {
        this.modalService.showWarningModal("Are you sure you want to discard changes?")
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                concatMap((isYesClicked: boolean) => {
                    if (!isYesClicked) return EMPTY;

                    this.changeOrderNotificationForm().form.markAsPristine();

                    if (this.notificationViewModel.notificationId > 0) {
                        this.notificationViewModel.setProperties(this.notification, this.communityOptions, this.subGroupOptions);
                        this.setCheckboxes();
                        return this.onHandleSubGroupChange$();
                    } else {
                        this.changeOrderNotificationForm().form.reset();
                        this.notificationViewModel = new NotificationViewModel();
                        this.notification = new Notification();
                        return of(null);
                    }
                }))
            .subscribe(() => {
                if (!this.notificationViewModel.selectedSubGroup) {
                    this.isSwitchOn = false; // default to Communities;
                } else {
                    this.isSwitchOn = true; // default to Subgroups
                }
            });
    }

    onSave(): void {
        this.notificationViewModel.mapEdhPropertiesToPcpProperties(this.market.orgId);
        this.notification = this.notificationViewModel.getNotification();

        if ((!this.allCommChbx && this.notificationViewModel.selectedCommunities.length === 0 &&
            !this.notificationViewModel.selectedSubGroup)) {
            this.errorMessage = 'Please select at least one item in each section.';
        } else {
            this.notificationService.saveNotification(this.notification)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe((response) => {
                    this.complete.emit(true);
                    this.changeOrderNotificationForm().form.reset();
                });
        }
    }
}
